import React from 'react';
import { motion } from 'framer-motion';
import { Plus, X, Car, Bike, Home, Tag } from 'lucide-react';
import { useCanEdit, useDeck } from './store';
import { DEFAULT_EXAMPLES, Example, TAGS } from './slides';
import { Editable } from './Editable';
import { SourceLine } from './SourceLine';

export function TagPicker({ sid, dark }: { sid: string; dark?: boolean }) {
  const { get, set } = useDeck();
  const canEdit = useCanEdit();
  const tags: string[] = get(`${sid}:tags`) || [];
  if (!canEdit) return null;
  const toggle = (t: string) => set(`${sid}:tags`, tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);
  return (
    <div className="flex items-center gap-1.5 font-ui" data-testid={`tags-${sid}`}>
      <Tag size={13} className={dark ? 'text-white/50' : 'text-black/40'} />
      {TAGS.map((t) => (
        <button
          key={t.id}
          data-testid={`tag-${sid}-${t.id}`}
          onClick={() => toggle(t.id)}
          className={`rounded-full border px-2.5 py-[2px] text-[12px] font-semibold transition-colors ${tags.includes(t.id) ? 'border-blue-600 bg-blue-600 text-white' : dark ? 'border-white/30 text-white/60 hover:border-white' : 'border-black/25 text-black/50 hover:border-black'}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

const ICONS = [Car, Bike, Home];

export function Examples({ sid, variant }: { sid: string; variant: 'app' | 'dossier' }) {
  const { get, set } = useDeck();
  const canEdit = useCanEdit();
  const list: Example[] = get(`${sid}:examples`) || DEFAULT_EXAMPLES;
  const save = (l: Example[]) => set(`${sid}:examples`, l);
  const rename = (id: string, name: string) => save(list.map((e) => (e.id === id ? { ...e, name } : e)));
  const add = () => save([...list, { id: `ex${Date.now().toString(36)}`, name: 'Exemple' }]);
  const app = variant === 'app';

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid={`examples-${sid}`}>
      {list.map((e, i) => {
        const I = ICONS[i % 3];
        return (
          <motion.div
            key={e.id}
            initial={app ? { y: 80, opacity: 0 } : { x: 40, opacity: 0, rotate: 0 }}
            animate={app ? { y: 0, opacity: 1 } : { x: 0, opacity: 1, rotate: i % 2 ? 1.2 : -1 }}
            transition={app ? { type: 'spring', stiffness: 320, damping: 30, delay: 0.15 + i * 0.08 } : { duration: 0.4, delay: 0.5 + i * 0.08 }}
            className={app
              ? 'relative flex min-h-0 flex-1 gap-4 rounded-2xl border border-neutral-200 bg-white p-4 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.25)]'
              : 'relative flex min-h-0 flex-1 flex-col rounded-[4px] border border-[#8a6a3f]/40 bg-[#efe2c4] p-3 pl-9 shadow-[0_8px_18px_-8px_rgba(50,25,0,0.6)]'}
            data-testid={`example-${sid}-${e.id}`}
          >
            {app ? (
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${e.main ? 'bg-black text-white' : 'bg-neutral-100 text-neutral-700'}`}><I size={22} /></div>
            ) : (
              <span className="absolute left-3 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-[#8a6a3f] bg-[#b48a5a]" />
            )}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                {!app && <span className="font-elite text-[12px] tracking-widest text-red-700">SCELLÉ N°{i + 1}</span>}
                {canEdit ? (
                  <input
                    data-testid={`example-name-${sid}-${e.id}`}
                    value={e.name}
                    onKeyDown={(ev) => ev.stopPropagation()}
                    onChange={(ev) => rename(e.id, ev.target.value)}
                    className={`min-w-0 flex-1 bg-transparent outline-none ${app ? 'font-app text-[19px] font-bold' : 'font-elite text-[18px]'}`}
                  />
                ) : (
                  <span className={app ? 'font-app text-[19px] font-bold' : 'font-elite text-[18px]'}>{e.name}</span>
                )}
                {app && <span className={`rounded-full px-2 py-[1px] font-ui text-[11px] font-semibold ${e.main ? 'bg-blue-600 text-white' : 'bg-neutral-100 text-neutral-500'}`}>{e.main ? 'Exemple principal' : 'Secondaire'}</span>}
                {canEdit && (
                  <button data-testid={`example-delete-${sid}-${e.id}`} onClick={() => save(list.filter((x) => x.id !== e.id))} className="rounded-full p-1 text-neutral-500 hover:bg-red-600 hover:text-white"><X size={13} /></button>
                )}
              </div>
              <Editable k={`${sid}:ex:${e.id}`} ph={`Ce que montre l'exemple ${e.name}…`} max={180} size={17} boxClass="flex-1" className={app ? 'font-ui text-neutral-700 leading-snug' : 'font-type text-neutral-800 leading-snug'} testId={`example-text-${sid}-${e.id}`} />
              <SourceLine k={`${sid}:ex:${e.id}:src`} parentKey={`${sid}:ex:${e.id}`} testId={`example-src-${sid}-${e.id}`} size={11} label={app ? 'Source' : 'Réf.'} />
            </div>
          </motion.div>
        );
      })}
      {canEdit && list.length < 3 && (
        <button data-testid={`example-add-${sid}`} onClick={add} className="flex items-center justify-center gap-1 rounded-xl border-2 border-dashed border-black/25 py-2 font-ui text-[13px] text-black/50 hover:border-black hover:text-black">
          <Plus size={14} /> Ajouter un exemple
        </button>
      )}
    </div>
  );
}
